import { useCallback, useEffect, useMemo, useRef } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { Account } from "@common/types/domains/accounting/account";
import { pluralize, pluralizeWithCount } from "@common/helpers/string";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CommandsDirectory } from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import { useAccounts } from "@hooks/useAccounts";
import { buildAccountCommandOptions } from "@hooks/useLineItemsBulkAccountCommandOptions";
import {
    useBulkSetCatalogItemsActive,
    useBulkUpdateCatalogItems,
} from "@api/catalogItem.api";
import { useClasses } from "@api/class.api";
import { useAccrualLedgerStatus } from "@api/ledger.api";
import {
    CatalogItemBulkActiveEligibility,
    getCatalogItemBulkActiveEligibility,
} from "./catalogItemBulkActive.helpers";
import { CatalogItemRow } from "./catalogItems.types";

interface UseCatalogItemBulkActionsParams {
    selectedRows: CatalogItemRow[];
    selectOnly: (ids: string[]) => void;
    hasClassesAccess: boolean;
    hasAccrualLedgerAccess: boolean;
    isSelectionStale: boolean;
}

type CatalogItemBulkPatch =
    | { classIds: string[] }
    | { revenueAccountId: string }
    | { receivablesAccountId: string };

interface BulkSetActiveAction {
    active: boolean;
    selectIds: (eligibility: CatalogItemBulkActiveEligibility) => string[];
    verb: string;
    doneLabel: string;
}

const ARCHIVE_ACTION: BulkSetActiveAction = {
    active: false,
    selectIds: ({ archivableIds }) => archivableIds,
    verb: "archive",
    doneLabel: "Archived",
};

const ENABLE_ACTION: BulkSetActiveAction = {
    active: true,
    selectIds: ({ enablableIds }) => enablableIds,
    verb: "enable",
    doneLabel: "Enabled",
};

function hasSameIds(rows: CatalogItemRow[], ids: string[]): boolean {
    const idSet = new Set(ids);
    return rows.length === idSet.size && rows.every((row) => idSet.has(row.id));
}

function getSingleEntityId(rows: CatalogItemRow[]): number | undefined {
    const [first] = rows;
    const isSingleEntitySelection =
        first !== undefined &&
        rows.every((row) => row.entityId === first.entityId);
    return isSingleEntitySelection ? first.entityId : undefined;
}

export function useCatalogItemBulkActions({
    selectedRows,
    selectOnly,
    hasClassesAccess,
    hasAccrualLedgerAccess,
    isSelectionStale,
}: UseCatalogItemBulkActionsParams) {
    const {
        open: openCommands,
        close: closeCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();
    const { toast: successToast, error: errorToast } = useToaster();
    const { mutateAsync: bulkUpdateCatalogItems, isPending } =
        useBulkUpdateCatalogItems();
    const {
        mutateAsync: bulkSetCatalogItemsActive,
        isPending: isSettingActive,
    } = useBulkSetCatalogItemsActive();
    const { classesWithChildren, classesMap } = useClasses();

    const isBulkActionDisabled =
        isPending || isSettingActive || isSelectionStale;
    const selectedRowsRef = useRef(selectedRows);
    selectedRowsRef.current = selectedRows;
    const isBulkActionDisabledRef = useRef(isBulkActionDisabled);
    isBulkActionDisabledRef.current = isBulkActionDisabled;

    const accountEntityId = getSingleEntityId(selectedRows);
    const { accounts } = useAccounts({
        entityId: accountEntityId,
        excludeLinkedToFinancialAccount: true,
    });
    const { hasAccrualLedger } = useAccrualLedgerStatus(accountEntityId);

    const runBulkUpdate = useCallback(
        async ({
            patch,
            entityId,
        }: {
            patch: CatalogItemBulkPatch;
            entityId?: number;
        }) => {
            const rows = selectedRowsRef.current;
            if (rows.length === 0 || isBulkActionDisabledRef.current) {
                return;
            }
            if (
                entityId !== undefined &&
                getSingleEntityId(rows) !== entityId
            ) {
                errorToast("Selection changed, reopen actions to update it");
                return;
            }
            closeCommands();
            try {
                const { succeededIds, failedIds } =
                    await bulkUpdateCatalogItems({
                        catalogItemIds: rows.map((row) => row.id),
                        body: patch,
                    });
                if (succeededIds.length > 0) {
                    successToast(
                        `Updated ${succeededIds.length} ${pluralize("item", succeededIds.length)}`,
                    );
                }
                if (failedIds.length > 0) {
                    errorToast(
                        `Failed to update ${failedIds.length} ${pluralize("item", failedIds.length)}`,
                    );
                }
                selectOnly(failedIds);
            } catch {
                errorToast(
                    `Failed to update ${rows.length} ${pluralize("item", rows.length)}`,
                );
            }
        },
        [
            closeCommands,
            bulkUpdateCatalogItems,
            successToast,
            errorToast,
            selectOnly,
        ],
    );

    const runBulkSetActive = useCallback(
        async ({ active, selectIds, verb, doneLabel }: BulkSetActiveAction) => {
            if (isBulkActionDisabledRef.current) {
                return;
            }
            const rows = selectedRowsRef.current;
            const catalogItemIds = selectIds(
                getCatalogItemBulkActiveEligibility(rows),
            );
            if (catalogItemIds.length === 0) {
                return;
            }
            const requestSelectionIds = rows.map((row) => row.id);
            closeCommands();
            try {
                const { updatedIds, conflictIds } =
                    await bulkSetCatalogItemsActive({
                        catalogItemIds,
                        active,
                    });
                if (updatedIds.length > 0) {
                    successToast(
                        `${doneLabel} ${pluralizeWithCount("item", updatedIds.length)}`,
                    );
                }
                if (conflictIds.length > 0) {
                    errorToast(
                        `Couldn't enable ${pluralizeWithCount("item", conflictIds.length)}: an active item with the same name already exists`,
                    );
                }
                if (hasSameIds(selectedRowsRef.current, requestSelectionIds)) {
                    selectOnly(conflictIds);
                }
            } catch {
                errorToast(
                    `Failed to ${verb} ${pluralizeWithCount("item", catalogItemIds.length)}`,
                );
            }
        },
        [
            closeCommands,
            bulkSetCatalogItemsActive,
            successToast,
            errorToast,
            selectOnly,
        ],
    );
    const archiveSelected = useCallback(
        () => runBulkSetActive(ARCHIVE_ACTION),
        [runBulkSetActive],
    );
    const enableSelected = useCallback(
        () => runBulkSetActive(ENABLE_ACTION),
        [runBulkSetActive],
    );

    const applyClasses = useCallback(
        (classIds: string[]) => runBulkUpdate({ patch: { classIds } }),
        [runBulkUpdate],
    );

    const incomeAccountOptions = useMemo(
        () =>
            accountEntityId === undefined
                ? []
                : buildAccountCommandOptions({
                      accounts,
                      idPrefix: "catalog-item-income",
                      onSelect: (account: Account) =>
                          void runBulkUpdate({
                              patch: { revenueAccountId: account.id },
                              entityId: accountEntityId,
                          }),
                  }),
        [accounts, accountEntityId, runBulkUpdate],
    );

    const canSetReceivables =
        hasAccrualLedgerAccess &&
        hasAccrualLedger &&
        accountEntityId !== undefined;
    const receivablesAccountOptions = useMemo(
        () =>
            canSetReceivables
                ? buildAccountCommandOptions({
                      accounts: accounts.filter(
                          (account) =>
                              account.type === AccountType.accountsReceivable,
                      ),
                      idPrefix: "catalog-item-receivables",
                      onSelect: (account: Account) =>
                          void runBulkUpdate({
                              patch: { receivablesAccountId: account.id },
                              entityId: accountEntityId,
                          }),
                  })
                : [],
        [accounts, accountEntityId, canSetReceivables, runBulkUpdate],
    );

    const handleOpenBulkActions = useCallback(() => {
        if (selectedRows.length === 0 || isBulkActionDisabled) {
            return;
        }
        const { archivableIds, enablableIds } =
            getCatalogItemBulkActiveEligibility(selectedRows);
        openCommands(CommandsDirectory.CATALOG_ITEM_BULK, {
            selectedCatalogItemCount: selectedRows.length,
            catalogItemBulkArchiveCount: archivableIds.length,
            catalogItemBulkEnableCount: enablableIds.length,
            onCatalogItemBulkArchive:
                archivableIds.length > 0 ? archiveSelected : undefined,
            onCatalogItemBulkEnable:
                enablableIds.length > 0 ? enableSelected : undefined,
            catalogItemBulkClassTree: hasClassesAccess
                ? classesWithChildren
                : undefined,
            catalogItemBulkClassesMap: hasClassesAccess
                ? classesMap
                : undefined,
            catalogItemApplyClasses: hasClassesAccess
                ? applyClasses
                : undefined,
            catalogItemBulkIncomeAccountOptions:
                incomeAccountOptions.length > 0
                    ? incomeAccountOptions
                    : undefined,
            catalogItemBulkReceivablesAccountOptions:
                receivablesAccountOptions.length > 0
                    ? receivablesAccountOptions
                    : undefined,
        });
    }, [
        openCommands,
        selectedRows,
        archiveSelected,
        enableSelected,
        isBulkActionDisabled,
        hasClassesAccess,
        classesWithChildren,
        classesMap,
        applyClasses,
        incomeAccountOptions,
        receivablesAccountOptions,
    ]);

    const handleOpenRef = useRef(handleOpenBulkActions);
    handleOpenRef.current = handleOpenBulkActions;

    const hasSelection = selectedRows.length > 0;
    useEffect(() => {
        if (!hasSelection) {
            return;
        }
        registerOpenOverride(() => handleOpenRef.current());
        return () => unregisterOpenOverride();
    }, [hasSelection, registerOpenOverride, unregisterOpenOverride]);

    return {
        handleOpenBulkActions,
        isBulkActionDisabled,
    };
}
